// ─── Shared FieldRow for sync conflict views ────────────────────────────────
//
// Renders a single field comparison row: label | client value | server value.
// Tri-state tint: amber (changed), emerald (client-only), sky (server-only).
//
// Desktop: uses semantic <tr>/<td> for screen reader column header announcement.
// Mobile (stacked=true): renders a stacked div with labeled client/server values.

import { formatValue, fieldChanged } from '@bbpos/utils';
import type { FieldSpec } from '@bbpos/utils';
import type { CurrencyCode } from '@bbpos/types';
import { cn } from '../../../constants/design-tokens';

export interface FieldRowProps {
  spec: FieldSpec;
  clientVersion: Record<string, unknown>;
  serverVersion: Record<string, unknown>;
  stacked?: boolean;
  /**
   * Tenant currency for `currency`-format fields. Defaults to `'INR'`, keeping
   * every existing consumer byte-identical; non-INR threads the symbol/subunit/
   * decimals through `formatValue`.
   */
  currency?: CurrencyCode;
}

function useFieldState(
  spec: FieldSpec,
  clientVersion: Record<string, unknown>,
  serverVersion: Record<string, unknown>,
  currency: CurrencyCode,
) {
  const hasClient = spec.key in clientVersion;
  const hasServer = spec.key in serverVersion;
  const isChanged = hasClient && hasServer && fieldChanged(spec.key, clientVersion, serverVersion);
  const isClientOnly = hasClient && !hasServer;
  const isServerOnly = !hasClient && hasServer;

  const clientText = formatValue(spec.format, clientVersion[spec.key], currency);
  const serverText = formatValue(spec.format, serverVersion[spec.key], currency);

  let tint = '';
  if (isChanged) tint = 'bg-amber-50/60 dark:bg-amber-900/10';
  else if (isClientOnly) tint = 'bg-emerald-50/60 dark:bg-emerald-900/10';
  else if (isServerOnly) tint = 'bg-sky-50/60 dark:bg-sky-900/10';

  return { hasClient, hasServer, isChanged, isClientOnly, isServerOnly, clientText, serverText, tint };
}

function FieldLabel({ spec, isChanged, isClientOnly, isServerOnly }: {
  spec: FieldSpec;
  isChanged: boolean;
  isClientOnly: boolean;
  isServerOnly: boolean;
}) {
  return (
    <span className="flex items-center gap-1.5">
      <span>{spec.label}</span>
      {isChanged && (
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-label="changed" />
      )}
      {isClientOnly && (
        <span className="text-[9px] font-semibold text-emerald-600 dark:text-emerald-400">
          +client
        </span>
      )}
      {isServerOnly && (
        <span className="text-[9px] font-semibold text-sky-600 dark:text-sky-400">
          +server
        </span>
      )}
    </span>
  );
}

export function FieldRow({
  spec,
  clientVersion,
  serverVersion,
  stacked,
  currency = 'INR',
}: FieldRowProps) {
  const { isChanged, isClientOnly, isServerOnly, clientText, serverText, tint } =
    useFieldState(spec, clientVersion, serverVersion, currency);

  if (stacked) {
    return (
      <div className={cn('rounded-lg px-3 py-2 transition-colors', tint)}>
        <div className="text-[11px] uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">
          <FieldLabel spec={spec} isChanged={isChanged} isClientOnly={isClientOnly} isServerOnly={isServerOnly} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <div className="text-[9px] uppercase tracking-wider text-emerald-600 dark:text-emerald-400 mb-0.5">Client</div>
            <div className="font-mono text-sm tabular-nums text-neutral-900 dark:text-neutral-100 break-words">
              {clientText}
            </div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-sky-600 dark:text-sky-400 mb-0.5">Server</div>
            <div className="font-mono text-sm tabular-nums text-neutral-700 dark:text-neutral-300 break-words">
              {serverText}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <tr className={cn('transition-colors', tint)}>
      <td className="px-3 py-2 text-[11px] uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
        <FieldLabel spec={spec} isChanged={isChanged} isClientOnly={isClientOnly} isServerOnly={isServerOnly} />
      </td>
      <td className="px-3 py-2 font-mono text-sm tabular-nums text-neutral-900 dark:text-neutral-100 break-words">
        {clientText}
      </td>
      <td className="px-3 py-2 font-mono text-sm tabular-nums text-neutral-700 dark:text-neutral-300 break-words">
        {serverText}
      </td>
    </tr>
  );
}
